import { css } from "@emotion/react";
import type { ProductVariantAvailabilityStatus } from "@swo/shared";
import type { EnrichedSwarovskiBundleProductFrontend, TranslatedValue } from "@swo/storefront";
import type React from "react";
import { useState } from "react";
import { FONTS, fontSize } from "../../styles/fonts";
import { BundleContentsEditor } from "./BundleContentsEditor";
import { BundleContentsList, toBundleProducts } from "./BundleContentsList";
import type { BundleVariantSelection } from "./BundleVariantSelection";
import { EditIcon } from "./EditIcon";
import { usePdpTranslations } from "./PdpTranslationsContext";
import { getPickerData } from "./PickerData";
import { T } from "../../components/T";

export const BDPVariantPicker: React.FC<{
  products: EnrichedSwarovskiBundleProductFrontend["products"];
  currentVariantsState: BundleVariantSelection;
  onBundleElementChanged: (_: { productKey: string; variantKey: string }) => void;
  countryIsoCode: string;
  translations: {
    yesLabel: TranslatedValue;
    noLabel: TranslatedValue;
  };
  priceLabel: TranslatedValue | string;
  availabilityMap: Map<string, ProductVariantAvailabilityStatus>;
}> = ({
  products,
  currentVariantsState,
  onBundleElementChanged,
  countryIsoCode,
  translations,
  priceLabel,
  availabilityMap,
}) => {
  const [isEditOpened, setEditOpened] = useState(false);

  const t = usePdpTranslations();

  const headline = t("bundleContents.headline");
  const editLabel = t("bundleContents.editLabel");
  const saveLabel = t("bundleContents.saveLabel");
  const closeLabel = t("bundleContents.closeLabel");

  function onEditClicked() {
    setEditOpened(_ => !_);
  }

  const bundleProducts = toBundleProducts(products, currentVariantsState, availabilityMap);

  const bundleEditorProducts = bundleProducts.map(p => ({
    product: p,
    pickerData: getPickerData(p, translations, countryIsoCode),
  }));

  const hasAnyChoice = bundleEditorProducts.some(p => p.pickerData.length >= 2);

  return (
    <div>
      <div
        css={css`
          display: flex;
          align-items: center;
          margin-bottom: 10px;
        `}
      >
        <span
          css={[
            fontSize.x2,
            FONTS.SwarovskiFutura500,
            css`
              text-transform: uppercase;
              display: block;
              margin-right: 32px;
            `,
          ]}
        >
          <T>{headline}</T>
        </span>

        {hasAnyChoice && (
          <button className="u-color-green u-flex u-align-center" onClick={onEditClicked}>
            <EditIcon />

            <span
              css={css`
                margin-left: 8px;
                text-transform: uppercase;
                text-decoration: underline;
              `}
            >
              {isEditOpened ? <T>{saveLabel}</T> : <T>{editLabel}</T>}
            </span>
          </button>
        )}
      </div>

      <BundleContentsList products={bundleProducts} countryIsoCode={countryIsoCode} translations={translations} />

      <BundleContentsEditor
        products={bundleEditorProducts}
        onClose={() => setEditOpened(false)}
        onBundleElementChanged={onBundleElementChanged}
        priceLabel={priceLabel}
        closeLabel={closeLabel}
        isOpened={isEditOpened}
      />
    </div>
  );
};
